import { tituloClinico } from "./textoClinico";

/* ============================================================================
   CITAS-COBERTURA · a qué pertenece el paciente, en una línea

   En la agenda sólo se veía el nombre, así que no se sabía si el paciente era
   particular, de seguro o de empresa hasta abrir la cita. Y al crear una cita,
   tras elegir al paciente tampoco se decía nada de su cobertura ni de su plan.

   Los tres datos vienen del MISMO sitio y ya existen en `pacientes`:
     tipo_seguro (NOT NULL) -> tipo_seguros.tipo   PARTICULAR/EMPRESAS/SEGUROS/Convenio
     seguro_id  (nullable)  -> seguros.nombre      la aseguradora o empresa concreta
     plan_id    (nullable)  -> plan.nombre         el plan asignado

   NADA SE CALCULA NI SE DEDUCE. Si un dato no viene, no se pinta: no se
   inventa "Particular" por omisión, porque un paciente sin `tipo_seguros`
   cargado no es lo mismo que un paciente particular.
   ========================================================================== */

/** Los cuatro tipos reales del sistema, normalizados para poder compararlos. */
const NORMALIZA = (v) =>
    String(v ?? "")
        .trim()
        .toLocaleUpperCase("es");

/**
 * La cobertura de un paciente, lista para pintar.
 *
 * @returns {null | { tipo, etiqueta, detalle, plan, tono }}
 *   `tipo`     -> el valor crudo del catálogo (PARTICULAR/SEGUROS/...)
 *   `etiqueta` -> lo que se pinta en corto: la aseguradora si la hay, y si no
 *                 el tipo. Cuando hay aseguradora, su nombre dice más que
 *                 "Seguros" y ocupa el mismo sitio.
 *   `detalle`  -> la línea larga, para el `title` y para las fichas anchas.
 *   `plan`     -> nombre del plan, o null.
 *   `tono`     -> "neutro" para particular, "info" para el resto. El color
 *                 distingue "paga él" de "lo cubre un tercero", que es la
 *                 pregunta real de caja.
 */
export function coberturaDe(paciente) {
    if (!paciente) return null;

    const tipo = paciente?.tipo_seguros?.tipo ?? null;
    const aseguradora = paciente?.seguros?.nombre ?? null;
    const plan = paciente?.plan?.nombre ?? null;

    // Sin ninguno de los dos no hay nada que decir. Un hueco es más honesto
    // que un "Particular" supuesto.
    if (!tipo && !aseguradora) return null;

    const esParticular = NORMALIZA(tipo) === "PARTICULAR";

    const etiqueta = aseguradora ? tituloClinico(aseguradora) : tituloClinico(tipo);

    // El detalle sólo repite el tipo cuando aporta algo: "Latinasalud
    // (Seguros)" informa; "Particular (Particular)" no.
    const detalle =
        aseguradora && tipo && NORMALIZA(aseguradora) !== NORMALIZA(tipo)
            ? `${tituloClinico(tipo)} · ${tituloClinico(aseguradora)}`
            : etiqueta;

    return {
        tipo,
        etiqueta,
        detalle,
        plan: plan ? tituloClinico(plan) : null,
        tono: esParticular ? "neutro" : "info",
    };
}

/**
 * La misma cobertura, pero partiendo de una CITA.
 *
 * La cita trae el paciente anidado (`cita.pacientes`), con los mismos campos
 * -- `ListarCitaDoctoresFechas` los incluye desde CITAS-COBERTURA.
 */
export function coberturaDeCita(cita) {
    return coberturaDe(cita?.pacientes);
}

/** Texto de una línea para el `title` de la tarjeta: cobertura y plan. */
export function textoCobertura(cobertura) {
    if (!cobertura) return "";
    return cobertura.plan ? `${cobertura.detalle} · plan ${cobertura.plan}` : cobertura.detalle;
}
